// MA-874 (operator D-1101-23) — the pure `child_attempts` row -> ChildAttempt mapper.
//
// Moved verbatim out of attemptLogger.ts so a server bundle (the weekly recap's
// api/_lib/recapDetail.bundle.mjs) can map rows without pulling the browser
// supabase client, Sentry or the network circuit breaker. attemptLogger imports
// it back and still exposes it as `__test.fromRow`; no behaviour change.
// Keep this module import-free apart from types.
import type { ChildAttempt } from "../../adaptation/types";

// ── snake_case row → camelCase ChildAttempt ──────────────────────────

export function fromRow(r: Record<string, any>): ChildAttempt {
  return {
    childId: r.child_id,
    sessionId: r.session_id,
    runId: r.run_id,
    timestamp: r.timestamp,
    ageBand: r.age_band,
    domain: r.domain,
    skillId: r.skill_id,
    itemId: r.item_id,
    itemType: r.item_type,
    correct: r.correct,
    selectedAnswer: r.selected_answer,
    expectedAnswer: r.expected_answer,
    responseChannel: r.response_channel,
    supportMode: r.support_mode ?? [],
    hintLevel: r.hint_level,
    scaffoldUsed: r.scaffold_used,
    readAloudUsed: r.read_aloud_used,
    pictureSupportUsed: r.picture_support_used,
    adultSupportUsed: r.adult_support_used,
    mascotSupportUsed: r.mascot_support_used,
    handwritingRequired: r.handwriting_required,
    handwritingRecognized: r.handwriting_recognized,
    handwritingConfidence: r.handwriting_confidence,
    numeralFormationIssue: r.numeral_formation_issue,
    oralResponse: r.oral_response,
    latencyMs: r.latency_ms,
    errorType: r.error_type,
    misconceptionId: r.misconception_id,
    isTransferTask: r.is_transfer_task,
    transferTaskId: r.transfer_task_id,
    transferType: r.transfer_type,
    isDelayedReview: r.is_delayed_review,
    contentLaunchStatus: r.content_launch_status,
    expertReviewNeeded: r.expert_review_needed,
    contentFile: r.content_file,
    generatorVersion: r.generator_version,
    validatorVersion: r.validator_version,
    curriculumVersion: r.curriculum_version,
    contentHash: r.content_hash,
    rawChildFacingLLM: r.raw_child_facing_llm,
    ...(r.frustration_signal ? { frustrationSignal: r.frustration_signal } : {}),
    ...(r.confidence_band ? { confidenceBand: r.confidence_band } : {}),
    // ── P-METADATA-EXPAND-A bedrock ──────────────────────────────────
    // Defensive defaults for legacy rows persisted before migration
    // 0006 (where these columns either don't exist OR exist with the
    // column default). Each field is ALWAYS-PRESENT on the read shape
    // so downstream code doesn't have to handle undefined vs null —
    // null is the "not available" sentinel for the 4 optional fields;
    // session_source defaults to "path" matching filterPathAttempts.
    sessionSource: (r.session_source ?? "path") as ChildAttempt["sessionSource"],
    subject: r.subject ?? null,
    targetGradeBand: r.target_grade_band ?? null,
    adaptiveReason: r.adaptive_reason ?? null,
    lastSuccessAt: r.last_success_at ?? null,
    // P-TIER3-INSTRUMENTATION-A — null for legacy rows persisted before
    // migration 0008 (column absent / default NULL).
    scaffoldTypeUsed: (r.scaffold_type_used ?? null) as ChildAttempt["scaffoldTypeUsed"],
    // P-TIER3-INSTRUMENTATION-B — defensive defaults for legacy rows persisted
    // before migration 0009 (columns absent). Numeric stubs read 0, booleans
    // false, mastery bands null — matching the column DEFAULTs.
    hintCount: r.hint_count ?? 0,
    audioReplayCount: r.audio_replay_count ?? 0,
    promptRepeatCount: r.prompt_repeat_count ?? 0,
    retryIndex: r.retry_index ?? 0,
    isRepetition: r.is_repetition ?? false,
    masteryBefore: (r.mastery_before ?? null) as ChildAttempt["masteryBefore"],
    difficultyTier: (r.difficulty_tier ?? 1) as ChildAttempt["difficultyTier"],
    masteryAfter: (r.mastery_after ?? null) as ChildAttempt["masteryAfter"],
    // ── PR-CANONICAL-EVIDENCE-A2 — canonical evidence columns (0010). ──
    // `?? undefined` (NOT `?? null`): undefined is MEANINGFUL — it identifies a
    // legacy row written before 0010. Wave B aggregators filter
    // `evidenceContractVersion >= 1`, so a null/absent column must read back as
    // undefined (excluded), never as a present-but-null value that could slip
    // an evidence-mode check. See canonicalEvidence.isIndependentAttempt.
    audienceMode: (r.audience_mode ?? undefined) as ChildAttempt["audienceMode"],
    evidenceContractVersion: r.evidence_contract_version ?? undefined,
    // ── PR-CANONICAL-EVIDENCE-A3.1 — answer_revealed column (0011). ─────
    // `?? undefined` (NOT `?? false`): undefined identifies a pre-A3 legacy row.
    // attemptKindFor keys on `=== true`, so undefined reads as not-revealed —
    // the same outcome as false, but the distinction is preserved for a future
    // Wave B aggregator that may want to tell "known-not-revealed" (false) from
    // "unknown" (undefined) via an evidence-contract-version bump. See the
    // migration 0011 header (D5.a / G1 resolution).
    answerRevealed: (r.answer_revealed ?? undefined) as ChildAttempt["answerRevealed"],
    // ── MA-14: idempotency key (migration 0015). ─────────────────────
    // undefined (not null) for pre-0015 legacy rows — mirrors the
    // audienceMode / evidenceContractVersion legacy-sentinel pattern.
    eventKey: (r.event_key ?? undefined) as string | undefined,
  };
}
